import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../config';

class HttpError extends Error {
  constructor(status) {
    super(`The server responded with ${status}.`);
    this.status = status;
  }
}

/**
 * Fetches one dashboard endpoint (`overview` or `mitra-overview`).
 * `forbidden` is true on 403 so the page can say "no access" instead of "error".
 *
 * `legacyLoader(getJson)` is used when the endpoint returns 404, i.e. the API that the
 * app points to has not been deployed with the new dashboard endpoints yet. It builds a
 * reduced payload from the older endpoints; the result is flagged `limited: true`.
 */
const useDashboardData = (endpoint, token, legacyLoader) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    if (!token) return;
    const id = ++requestId.current;
    setLoading(true);

    const getJson = async path => {
      const res = await fetch(`${api.API_URL}/dashboard/${path}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new HttpError(res.status);
      return res.json();
    };

    try {
      let result;
      try {
        result = await getJson(endpoint);
      } catch (err) {
        if (err.status !== 404 || !legacyLoader) throw err;
        result = { ...(await legacyLoader(getJson)), limited: true };
      }
      if (id !== requestId.current) return;
      setData(result);
      setForbidden(false);
      setError(null);
      setUpdatedAt(new Date());
    } catch (err) {
      if (id !== requestId.current) return;
      if (err.status === 403) {
        setForbidden(true);
        setError(null);
        return;
      }
      setError(err.message || 'The server could not be reached.');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [endpoint, token, legacyLoader]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, forbidden, updatedAt, reload: load };
};

export default useDashboardData;
